/**
 * A-07 プロフィール（FR-18・**D-63**）＝この卸に伝える自分の連絡先と、いつもの納品先。
 *
 * **なぜ卸ごとなのか**：ここに入れる氏名・電話・住所は、その卸の取引データとして扱う
 * （卸ごとに暗号化して保存する＝運営には返さない）。掛け持ちの方は新しい卸に接続したとき、
 * 直前に入れた内容が初期値として入るので、実際に打ち込むのは最初の1回だけで済む。
 *
 * 画面の文言は「なぜ要るか」を書く＝LINE の表示名は本名とは限らず、卸は誰からの注文か
 * 分からないことがある（監査 flow#4）。
 *
 * ★**M1.6（D-86/D-87）で本画面が持つものが3つになった**：
 *   ①この卸に伝える自分の情報（従来どおり**卸ごと**）＝**お名前は必須**（D-87）
 *   ②現場アドレス帳（`AddressBook`）＝**卸に依らない**（工務店グループで共有）
 *   ③工務店グループ（`BuilderGroup`）＝②を共有する単位
 *   ①だけが卸ごと・②③は自分たちのもの、という違いを見出しで言い切る（混ぜると
 *   「卸を切り替えたら現場が消えた」に見える）。
 *
 * ★**M1.8（D-96⑦）＝どの卸の情報を編集するかは、この画面の中のチップで選ぶ**。以前はヘッダーの
 *   卸セレクタ（絞り込み）が兼ねていたが、絞り込みごと撤去した。**チップは2社以上つないで
 *   いるときだけ出す**（1社なら選ぶ余地が無い）。既定は接続の古い方＝ヘッダーが選んでいた値と同じ。
 */

import { Button, Card, Chip, ErrorNote, Spinner } from "@kenzai/ui";
import { partnerApi, type BuilderProfile, type ConnectedTenant } from "@kenzai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

import { AddressBook, Ico } from "./AddressBook";
import { BuilderGroup } from "./BuilderGroup";
import { Input } from "./formKit";
import { PageTitle } from "../Shell";

export function Profile({
  api,
  connections,
}: {
  api: ReturnType<typeof partnerApi>;
  connections: ConnectedTenant[];
}) {
  const qc = useQueryClient();
  // 編集対象の卸（既定＝接続の古い方）。消えた接続を指したままにしない。
  const [picked, setPicked] = useState<number | null>(null);
  const tenant = connections.find((c) => c.tenant_id === picked) ?? connections[0];
  const tenantId = tenant.tenant_id;
  const tenantName = tenant.tenant_name;
  const q = useQuery({ queryKey: ["profile", tenantId], queryFn: () => api.profile(tenantId) });
  // 冒頭の「いまの状態」カード用。queryKey は `BuilderGroup` と共有＝二重に取りに行かない。
  const company = useQuery({ queryKey: ["builder-company"], queryFn: () => api.company() });

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [saved, setSaved] = useState(false);
  const [nameError, setNameError] = useState(false);

  // 取得できたら（＝卸を切り替えたときも）フォームに流し込む。
  // ★**ここで `saved` を触らない**：保存に成功するとキャッシュを差し替える＝この effect が
  //   走り直すので、`setSaved(false)` を入れると「保存しました」が出た瞬間に消える（実際踏んだ）。
  //   代わりに、利用者が入力を変えたときに消す（＝表示が実態とずれない）。
  useEffect(() => {
    const p = q.data;
    if (!p) return;
    setName(p.person_name ?? "");
    setPhone(p.phone ?? "");
    setAddress(p.address ?? "");
  }, [q.data]);

  const edit = (setter: (v: string) => void) => (v: string) => {
    setSaved(false);
    setter(v);
  };

  const save = useMutation({
    mutationFn: (current: BuilderProfile) =>
      api.updateProfile(tenantId, {
        // ★お名前は**空にできない**（D-87）＝送信前に弾く。ここを null で通すと、
        //   一度登録した人が「消してしまう」経路ができ、次の注文がチェックアウトで止まる。
        person_name: name.trim(),
        phone: phone.trim() === "" ? null : phone.trim(),
        address: address.trim() === "" ? null : address.trim(),
        updated_at: current.updated_at,
      }),
    onSuccess: (next) => {
      qc.setQueryData(["profile", tenantId], next);
      setSaved(true);
    },
  });

  const current = q.data;

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-4)" }}>
      <PageTitle>プロフィール</PageTitle>

      {/* いまの状態を1枚で（Design `.pf-card` / `.pf-row`）＝**表示専用**。
          会社の代表者・所在地・メールは当システムが持っていないので出さない（Design にはある）。 */}
      <div className="pf-card" data-testid="profile-summary">
        <div className="pf-h">いまのご利用状況</div>
        <div className="pf-row">
          <span>取引中の卸</span>
          <b className="kz-num">{connections.length}社</b>
        </div>
        {company.data && (
          <div className="pf-row">
            <span>工務店グループ</span>
            <b style={{ minWidth: 0, overflowWrap: "anywhere" }}>{company.data.name}</b>
          </div>
        )}
      </div>

      {/* ★対象の卸を選ぶチップ（D-96⑦）＝**カードの外に置く**。中に入れると、卸を切り替えた
          瞬間に「登録内容の取得中」でカードごとスピナーに差し替わり、いま押したチップが指の下から
          消える（次の卸を押せない）。ここだけは残す。 */}
      {connections.length > 1 && (
        <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
          <span style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
            情報を登録するお取引先
          </span>
          <div className="kz-chiprow" role="group" aria-label="情報を登録するお取引先">
            {connections.map((c) => (
              <Chip
                key={c.tenant_id}
                on={c.tenant_id === tenantId}
                data-testid={`profile-tenant-${c.tenant_id}`}
                onClick={() => {
                  setPicked(c.tenant_id);
                  // 別の卸の「保存しました」を持ち越さない（何を保存したのか分からなくなる）
                  setSaved(false);
                  setNameError(false);
                }}
                style={{ minHeight: 44, padding: "0 var(--kz-space-4)", display: "inline-flex", alignItems: "center" }}
              >
                {c.tenant_name}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {q.isError ? (
        <ErrorNote error={q.error} />
      ) : !current ? (
        <Spinner />
      ) : (
      <Card>
        <h2 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
          {tenantName} に伝える情報
        </h2>
        <p
          style={{
            margin: "0 0 var(--kz-space-4)",
            fontSize: "var(--kz-font-size-sm)",
            color: "var(--kz-color-ink-muted)",
          }}
        >
          注文を受けた担当者が、どなたからのご注文か・どこへお届けするかを分かるようにするための情報です。
          お取引先ごとに登録できます。
        </p>

        <Input
          label="お名前（必須）"
          value={name}
          onChange={(v) => {
            edit(setName)(v);
            if (v.trim() !== "") setNameError(false);
          }}
          maxLength={100}
          required
          placeholder="田中 悟"
          data-testid="profile-name"
        />
        {nameError && (
          <p
            role="alert"
            data-testid="profile-name-error"
            style={{ margin: "0 0 var(--kz-space-3)", color: "var(--kz-status-danger)", fontSize: "var(--kz-font-size-sm)" }}
          >
            お名前をご入力ください。ご注文のときに必要です。
          </p>
        )}
        <Input
          label="電話番号"
          value={phone}
          onChange={edit(setPhone)}
          type="tel"
          inputMode="tel"
          maxLength={30}
          placeholder="090-1234-5678"
          data-testid="profile-phone"
        />
        <Input
          label="いつものお届け先"
          value={address}
          onChange={edit(setAddress)}
          maxLength={200}
          placeholder="千葉県柏市◯◯町1-2-3"
          hint="ご注文のときに現場が一覧に無ければ、ここの住所が初期値として入ります。"
          data-testid="profile-address"
        />

        {save.isError && <ErrorNote error={save.error} />}
        <div style={{ marginTop: "var(--kz-space-4)" }}>
          <Button
            fullWidth
            data-testid="profile-save"
            disabled={save.isPending}
            onClick={() => {
              // disabled にせず**押したら理由を言う**（押せない理由が出ないまま止めない）
              if (name.trim() === "") {
                setNameError(true);
                return;
              }
              save.mutate(current);
            }}
          >
            {save.isPending ? "保存しています…" : "保存する"}
          </Button>
        </div>
        {saved && (
          <p data-testid="profile-saved" style={{ marginTop: "var(--kz-space-3)", color: "var(--kz-status-ok)" }}>
            保存しました。
          </p>
        )}
      </Card>
      )}

      {/* ★ここから下は**卸に依らない**（自分たちのもの）＝FR-129・D-86。
          上の「{tenantName} に伝える情報」と混ぜないよう、カードを分けて見出しで言い切る。 */}
      <AddressBook client={api} />
      <BuilderGroup client={api} />

      {/* ★D-80 で下部タブを4つにした分、ここが**現場・原価と卸一覧の入口**になる。
          毎日は開かないが、消すと工務店LP の約束（現場ごとの材料費）に辿り着けなくなる。
          ★M1.9b＝Design `.pf-card.links` の行リンク型（divider 区切り＋右 chevron）に統一。
            行き先・文言・testid は不変。 */}
      <nav className="pf-card links" style={{ marginTop: "var(--kz-space-3)" }}>
        <Link className="pf-link" to="/sites" data-testid="to-sites" style={{ textDecoration: "none" }}>
          現場・原価管理（現場ごとの材料費）
          <Ico name="chevR" size={15} strokeWidth={2.2} />
        </Link>
        <Link className="pf-link" to="/switch" data-testid="to-switch" style={{ textDecoration: "none" }}>
          新規卸追加（招待コードで接続）
          <Ico name="chevR" size={15} strokeWidth={2.2} />
        </Link>
      </nav>
    </div>
  );
}
